import React, { useCallback } from "react";
import { Box, makeStyles, Typography } from "@material-ui/core";
import { usePlaidLink } from "react-plaid-link";

import { Button, Image } from "components";
import { formatMoney, navigateTo } from "lib/helpers";
import { verifyInvestorWithPlaid } from "services/users";

const useStyles = makeStyles(theme => ({
  logo: {
    height: "55px !important",
  },
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    paddingBottom: theme.spacing(3),
  },
}));

interface VerificationConfirmationProps {
  linkToken: string;
  minimumBalance: number;
}

const VerificationConfirmation: React.FC<VerificationConfirmationProps> = ({
  linkToken,
  minimumBalance,
}) => {
  const classes = useStyles();
  const [verified, setVerified] = React.useState<boolean>(false);

  // add metadata parameter to see full response
  const onSuccess = useCallback((token: string) => {
    verifyInvestorWithPlaid(token)
      .then(response => {
        if (response.verified === true) {
          return setVerified(true);
        } else {
          alert(response.message);
        }
      })
      .catch((reason: Error) => {
        alert(reason);
      });
  }, []);

  const config = {
    token: linkToken,
    onSuccess,
  };

  const { open, ready, error } = usePlaidLink(config);

  const VerifiedConfirmation = (
    <>
      <Box padding={3}>
        <Box width="100%" className="title -main">
          <Typography
            style={{ display: "block" }}
            color="primary"
            variant="inherit"
            align="center"
          >
            Verification completed!
          </Typography>
        </Box>
      </Box>
      <Box padding={3}>
        <Typography align="center" color="primary">
          You are now able to place bids on auctions and offers on listings.
        </Typography>
      </Box>
      <Box display="flex" justifyContent="center" width="100%">
        <Button
          customColor="green"
          variant="contained"
          onClick={() => navigateTo("/overview")}
        >
          See Current Listings
        </Button>
      </Box>
    </>
  );

  return (
    <Box className={classes.container}>
      <section className="es-small-hero normal-nav -no-image">
        <Box display="flex" justifyContent="center" width="100%">
          <Image name="re-logo.svg" className={classes.logo} />
        </Box>
        <Box width="100%" className="title -main">
          <Box padding={3} textAlign="center">
            Royalty Exchange Investor Verification
          </Box>
        </Box>
      </section>

      <Box padding={3} maxWidth={700} alignSelf="center">
        <Typography align="center" color="primary">
          This page will verify your eligibility to invest in music royalties by
          checking for a minimum account balance of $
          {formatMoney(minimumBalance, 0)}. Securely sign in using your online
          banking account. We use Plaid to verify your account balance. Your
          bank account details are never stored or shared with Royalty Exchange.
        </Typography>
      </Box>

      {verified && VerifiedConfirmation}

      {!verified && (
        <Box display="flex" justifyContent="center" width="100%">
          <Button
            customColor="green"
            variant="contained"
            onClick={() => open()}
            disabled={!ready || !!error}
          >
            Verify Investor Eligibility
          </Button>
        </Box>
      )}
      <Typography>{error?.message}</Typography>
    </Box>
  );
};

export default VerificationConfirmation;
